import { useEffect, useState, useMemo, useCallback } from 'preact/hooks';
import clsx from 'clsx';
import { useQueryLocation, useMediaQuery, useAuth, useWorkoutImage } from '../hooks';
import useStorefrontAPI from '../hooks/use-storefront-api';
import { STOREFRONT_QUERIES, transformVideos, extractCategories } from '../lib/storefrontClient';
import { getHabitTrackerData } from '../lib/habitTrackerHelpers';
import VideoModal from '../components/VideoModal';
import CompletionModal from '../components/CompletionModal';
import StreakStats from '../components/StreakStats';
import CustomSelect from '../components/CustomSelect';
import VideoCard from '../components/VideoCard';
import WorkoutImage from '../components/WorkoutImage';
import Link from '../components/Link';
import Header from '../components/Header';
import { filterAndSortVideos } from '../utils/video-filters';

function VideoLibrary() {
    const [currentCategory, setCurrentCategory] = useState('All');
    const [data, setData] = useState(false);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [completedVideoId, setCompletedVideoId] = useState(null);
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const [location] = useQueryLocation();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const isTablet = useMediaQuery('(min-width: 769px) and (max-width: 1024px)');
    const isTabletPortrait = useMediaQuery('(min-width: 769px) and (max-width: 1024px) and (orientation: portrait)');
    
    const { isAuthenticated, isLoading: authLoading, logout, hasActiveSubscription, subscriptionLoading } = useAuth();
    
    // Get shared workout image and video
    const { thumbnailUrl: mostRecentVideoThumbnail, video: mostRecentVideo } = useWorkoutImage();
    
    const headingClasses = isMobile 
        ? "tw-text-black tw-font-['Poppins'] tw-text-[1.25em] tw-font-semibold tw-leading-[110%] tw-uppercase tw-m-0"
        : "heading-anton tw-text-[32px]";
    const responsivePaddingClasses = (isMobile || isTabletPortrait) ? 'tw-px-2.5' : 'tw-px-5';

    // Fetch videos from Storefront API (fetch more to ensure we get recent ones, then filter/sort client-side)
    const [videoData, loading, videoError] = useStorefrontAPI(
        STOREFRONT_QUERIES.GET_VIDEOS,
        { first: 100 }
    );

    // Fetch habit tracker data from Customer Account API
    const [habitTrackerData, setHabitTrackerData] = useState({ currentStreak: 0, personalRecord: 0 });
    const [habitTrackerLoading, setHabitTrackerLoading] = useState(true);
    const [dataRefreshKey, setDataRefreshKey] = useState(0);

    // Check for completed workout and trigger refresh
    useEffect(() => {
        const completedDate = sessionStorage.getItem('completedWorkoutDate');
        if (completedDate) {
            // Clear the flag so we don't keep refreshing
            sessionStorage.removeItem('completedWorkoutDate');
            // Trigger a data refresh
            setDataRefreshKey(prev => prev + 1);
        }
    }, []);

    useEffect(() => {
        if (isAuthenticated && !authLoading) {
            getHabitTrackerData()
                .then(data => {
                    setHabitTrackerData(data);
                    setHabitTrackerLoading(false);
                })
                .catch(err => {
                    console.error('Failed to load habit tracker data:', err);
                    setHabitTrackerLoading(false);
                });
        }
    }, [isAuthenticated, authLoading, dataRefreshKey]);

    // Transform and set video data
    useEffect(() => {
        if (videoData?.metaobjects) {
            // transformVideos is now async, so we need to await it
            transformVideos(videoData.metaobjects).then(videos => {
                const categories = extractCategories(videos);

                setData({
                    videos,
                    categories,
                    title: 'Workout Library'
                });
            }).catch(err => {
                console.error('Error transforming videos:', err);
            });
        } else if (videoError) {
            console.error('Error loading videos:', videoError);
        }
    }, [videoData, videoError]);

    // Show loading state for auth and subscription
    if (authLoading || subscriptionLoading) {
        return (
            <div className="tw-w-full tw-p-5 tw-text-center">
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-[400px]">
                    <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-gray-900 tw-mb-4"></div>
                    <p>Loading...</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className="tw-w-full tw-p-5 tw-text-center">
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-[400px]">
                    <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-gray-900 tw-mb-4"></div>
                    <p>Authenticating...</p>
                </div>
            </div>
        );
    }

    // Check for active subscription
    if (!hasActiveSubscription) {
        return (
            <div className="tw-w-full tw-flex tw-items-center tw-justify-center tw-min-h-screen tw-bg-gray-50">
                <div className="tw-max-w-md tw-w-full tw-mx-4">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-8 tw-text-center">
                        <div className="tw-mb-6">
                            <div className="tw-text-6xl tw-mb-4">🔒</div>
                            <h1 className="tw-text-3xl tw-font-bold tw-mb-2">Subscription Required</h1>
                            <p className="tw-text-gray-600">You need an active subscription to access the workout library and habit tracker.</p>
                        </div>
                        
                        <a 
                            href="https://lexijwellness.com/products/ljw-guided-workouts?variant=45633267237060&selling_plan=3552968900"
                            className="tw-inline-block tw-w-full tw-bg-black tw-text-white tw-py-3 tw-px-6 tw-rounded-lg tw-font-semibold tw-hover:bg-gray-800 tw-transition-colors tw-mb-4"
                        >
                            Get Subscription
                        </a>
                        
                        <button 
                            onClick={logout}
                            className="tw-w-full tw-bg-gray-200 tw-text-gray-800 tw-py-3 tw-px-6 tw-rounded-lg tw-font-semibold tw-hover:bg-gray-300 tw-transition-colors"
                        >
                            Sign Out
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Show loading state for videos
    if (loading || habitTrackerLoading) {
        return (
            <div className="tw-w-full tw-p-5 tw-text-center">
                Loading videos...
            </div>
        );
    }

    const isActive = (page) => {
        if (page === 'videos') return location === '/';
        if (page === 'badges') return location === '/badges';
        if (page === 'calendar') return location === '/calendar';
        return false;
    };

    const handleLogoutClick = useCallback(() => {
        setShowLogoutConfirm(true);
    }, []);

    const handleLogoutConfirm = useCallback(() => {
        setShowLogoutConfirm(false);
        logout();
    }, [logout]);

    const handleLogoutCancel = useCallback(() => {
        setShowLogoutConfirm(false);
    }, []);

    const navLinks = useMemo(() => [
        { href: '/', label: 'Videos', page: 'videos', roundedClass: 'tw-rounded-l-lg', internal: true },
        { href: '/badges', label: 'Badges', page: 'badges', internal: true },
        { href: '/calendar', label: 'Calendar', page: 'calendar', internal: true },
        { href: 'https://www.facebook.com/share/g/1ADj3sTSs8/', label: 'Facebook Group', page: null, internal: false },
        { href: '/account', label: 'Account', page: null, internal: false, onClick: () => { sessionStorage.removeItem('was_on_habit_tracker'); window.location.href = '/account'; } },
        { label: 'Logout', page: null, roundedClass: 'tw-rounded-r-lg', internal: false, onClick: handleLogoutClick }
    ], [handleLogoutClick]);

    // Memoize streak data to avoid recalculation
    const currentStreak = useMemo(() => 
        habitTrackerData?.currentStreak || 0, 
        [habitTrackerData?.currentStreak]
    );
    
    const personalRecord = useMemo(() => 
        habitTrackerData?.personalRecord || 0, 
        [habitTrackerData?.personalRecord]
    );

    if (!data) {
        return (
            <div className="tw-w-full tw-p-5 tw-text-center">
                Loading...
            </div>
        );
    }

    // Memoize destructured data
    const { categories = ['all'], videos: rawVideos = [] } = useMemo(() => data, [data]);
    
    // Filter videos for the current view - show 30 most recent videos regardless of month
    const videos = useMemo(
        () => filterAndSortVideos(rawVideos, 'All', 'newest', 30, undefined, false),
        [rawVideos]
    );
    
    // Auto-open modal if autoplay flag is set in sessionStorage
    useEffect(() => {
        const shouldAutoplay = sessionStorage.getItem('autoplayVideo') === 'true';
        
        if (shouldAutoplay && !selectedVideo) {
            // Use the video from the hook if available, otherwise fall back to first filtered video
            const videoToPlay = mostRecentVideo || (videos.length > 0 ? videos[0] : null);
            if (videoToPlay) {
                setSelectedVideo(videoToPlay);
            }
            // Clear the flag
            sessionStorage.removeItem('autoplayVideo');
        }
    }, [videos, selectedVideo, mostRecentVideo]);

    // Memoized handlers
    const handleStartWorkout = useCallback(() => {
        // Use the video from the hook if available, otherwise fall back to first filtered video
        const videoToPlay = mostRecentVideo || (videos.length > 0 ? videos[0] : null);
        if (videoToPlay) {
            setSelectedVideo(videoToPlay);
        }
    }, [mostRecentVideo, videos]);

    const handleVideoSelect = useCallback((video) => {
        setSelectedVideo(video);
    }, []);

    const handleCloseVideoModal = useCallback(() => {
        setSelectedVideo(null);
    }, []);

    const handleVideoEnd = useCallback(() => {
        setCompletedVideoId(selectedVideo?.id);
        setSelectedVideo(null);
        setShowCompletionModal(true);
    }, [selectedVideo?.id]);

    const handleCloseCompletionModal = useCallback(() => {
        setShowCompletionModal(false);
        setCompletedVideoId(null);
    }, []);

    return (
        <>
            {/* Header */}
            <Header currentStreak={currentStreak} />
            
            {/* Desktop Navigation Bar */}
            {!isMobile && (
                <nav className="tw-flex tw-justify-center tw-items-center tw-w-full tw-my-14 tw-p-0">
                    <div className="tw-flex tw-justify-center tw-items-center">
                        {navLinks.map((link, index) => 
                            link.internal ? (
                                <Link
                                    key={index}
                                    href={link.href} 
                                    className={clsx(
                                        "nav-link",
                                        link.roundedClass,
                                        link.page && isActive(link.page) ? "nav-link-active" : "nav-link-inactive"
                                    )}
                                >
                                    {link.label}
                                </Link>
                            ) : link.onClick ? (
                                <button
                                    key={index}
                                    onClick={link.onClick}
                                    className={clsx(
                                        "nav-link",
                                        link.roundedClass,
                                        "nav-link-inactive",
                                        "tw-cursor-pointer"
                                    )}
                                >
                                    {link.label}
                                </button>
                            ) : (
                                <a 
                                    key={index}
                                    href={link.href} 
                                    className={clsx(
                                        "nav-link",
                                        link.roundedClass,
                                        "nav-link-inactive"
                                    )}
                                >
                                    {link.label}
                                </a>
                            )
                        )}
                    </div>
                </nav>
            )}

            {/* Stats Section */}
            <div className={`tw-w-full tw-flex tw-justify-center ${responsivePaddingClasses}`}>
                <div className={`tw-h-auto tw-rounded-[20px] tw-bg-[#F7F8F8] ${(isMobile || isTabletPortrait) ? 'tw-w-full' : 'tw-w-full tw-max-w-[1233px]'}`}>
                    {(isMobile || isTabletPortrait) ? (
                        // Mobile Layout
                        <div className="tw-flex tw-flex-col tw-items-center tw-gap-5 tw-px-2.5 tw-py-[18px] tw-pb-[46px]">

                            {/* Streak Stats */}
                            <StreakStats 
                                currentStreak={currentStreak} 
                                personalRecord={personalRecord}
                                className="stats-card"
                            />

                            {/* Today's Workout Image */}
                            <div className="tw-flex tw-justify-center tw-w-full tw-px-4">
                                <div className="tw-flex tw-flex-col tw-items-start tw-w-full">
                                    <WorkoutImage 
                                        thumbnailUrl={mostRecentVideoThumbnail}
                                        className="tw-w-full tw-h-auto tw-max-h-[250px] tw-object-contain tw-rounded-lg" 
                                        onClick={handleStartWorkout}
                                    />
                                </div>
                            </div>

                            {/* CTA Section - Below image */}
                            <div className="tw-flex tw-justify-center tw-w-full tw-mt-5">
                                <div className="tw-flex tw-items-center tw-justify-between tw-w-full tw-max-w-[350px]">
                                    <h2 className={clsx("tw-text-black tw-font-['Anton'] tw-font-normal tw-leading-[110%] tw-uppercase tw-m-0", isMobile ? 'tw-text-[20px]' : 'tw-text-[32px]')}>
                                        {currentStreak > 0 
                                            ? "GET AFTER IT TODAY!"
                                            : "START A NEW STREAK!"
                                        }
                                    </h2>
                                    <button 
                                        onClick={handleStartWorkout}
                                        className="btn-action btn-action-sm tw-w-auto tw-min-h-auto"
                                    >
                                        START WORKOUT
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        // Desktop Layout
                        <div className="tw-flex tw-items-center tw-justify-between tw-gap-0 tw-flex-row">
                            <div className="tw-flex-1 tw-flex tw-flex-col tw-justify-between tw-items-start tw-h-[403px] tw-py-[18px] tw-pb-[46px] tw-relative">
                                <div className="tw-flex tw-flex-col tw-items-start">
                                    <StreakStats 
                                        currentStreak={currentStreak} 
                                        personalRecord={personalRecord}
                                        className={`tw-flex tw-flex-col tw-items-start tw-gap-7 tw-rounded-xl tw-bg-white tw-mx-[18px] tw-px-4 tw-py-7 ${isTabletPortrait ? 'tw-w-[800px]' : 'tw-w-[477px]'}`}
                                    />
                                </div>
                                <div className="tw-flex tw-flex-col tw-items-start tw-mt-auto">
                                    <div className="tw-ml-11 tw-text-left tw-flex tw-flex-col tw-items-start">
                                        <h2 className={clsx(headingClasses, 'tw-mb-4 tw-break-words tw-whitespace-normal tw-text-left tw-h-auto tw-w-[359px]')}>
                                            {currentStreak > 0 
                                                ? "DON'T LOSE YOUR STREAK! START TODAY'S WORKOUT"
                                                : "START A NEW STREAK TODAY!"
                                            }
                                        </h2>
                                        <button 
                                            onClick={handleStartWorkout}
                                            className={clsx(
                                                "btn-action btn-action-lg",
                                                isTabletPortrait ? 'tw-w-[500px]' : 'tw-w-[300px]'
                                            )}
                                        >
                                            START WORKOUT
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="tw-flex tw-flex-col tw-items-start">
                                <WorkoutImage 
                                    thumbnailUrl={mostRecentVideoThumbnail}
                                    className="tw-w-[717px] tw-h-[403px] tw-object-cover tw-rounded-lg" 
                                    onClick={handleStartWorkout}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Video Library Section */}
            <div className={`tw-max-w-[1200px] tw-mx-auto tw-flex tw-flex-col tw-items-start ${isMobile ? 'tw-px-2' : 'tw-px-8'} ${isTablet ? 'tw-max-w-[900px] tw-items-center' : ''}`}>
                <h1 className={`tw-text-black tw-text-left tw-font-['Anton'] tw-font-normal tw-leading-[100%] tw-mb-10 tw-uppercase ${isMobile ? 'tw-text-2xl tw-mb-4' : 'tw-text-4xl md:tw-text-6xl'}`}>
                    WORKOUT LIBRARY
                </h1>

                <div className="tw-w-full">
                    <Filters
                        categories={categories}
                        setCurrentCategory={setCurrentCategory}
                        currentCategory={currentCategory}
                        isMobile={isMobile}
                        isTablet={isTablet}
                    />

                    <div className="tw-flex tw-justify-center tw-w-full">
                        <Videos videos={videos} currentCategory={currentCategory} onVideoSelect={handleVideoSelect} />
                    </div>
                </div>
            </div>

            {/* Video Modal */}
            {selectedVideo && (
                <VideoModal 
                    video={selectedVideo} 
                    onClose={handleCloseVideoModal}
                    onVideoEnd={handleVideoEnd}
                />
            )}

            {/* Completion Modal */}
            {showCompletionModal && (
                <CompletionModal 
                    onClose={handleCloseCompletionModal} 
                    videoId={completedVideoId}
                />
            )}

            {/* Logout Confirmation Modal */}
            {showLogoutConfirm && (
                <div className="modal-overlay tw-z-[1002]" onClick={handleLogoutCancel}>
                    <div className="modal-container tw-items-center tw-text-center" onClick={(e) => e.stopPropagation()}>
                        <button 
                            onClick={handleLogoutCancel}
                            className="tw-absolute tw-top-6 tw-right-6 tw-bg-transparent tw-border-0 tw-text-3xl tw-cursor-pointer tw-text-gray-400 hover:tw-text-black"
                        >
                            ×
                        </button>

                        <h1 className="modal-title tw-mb-8">
                            LOGOUT
                        </h1>
                        
                        <p className="modal-text tw-text-2xl tw-mb-8">
                            Are you sure you want to logout?
                        </p>
                        
                        <button 
                            onClick={handleLogoutConfirm}
                            className="btn-primary tw-mb-4"
                        >
                            YES, LOGOUT
                        </button>
                        
                        <button 
                            onClick={handleLogoutCancel}
                            className="btn-secondary"
                        >
                            CANCEL
                        </button>
                    </div>
                </div>
            )}

        </>
    );
}

function Filters({ categories, setCurrentCategory, currentCategory, isMobile, isTablet }) {
    // Helper function to format category labels
    const formatLabel = (str) => {
        return str
            .split(/[_\s]+/) // Split by underscore or space
            .map(word => {
                // Special case for HIIT
                if (word.toLowerCase() === 'hiit') {
                    return 'HIIT';
                }
                // Capitalize first letter of each word
                return word.charAt(0).toUpperCase() + word.slice(1);
            })
            .join(' ');
    };

    // Define the exact desired order for categories
    // Order: HIIT, Arms & Back, Lower Body, Shoulders & Core, Full Body, Abs
    const categoryOrder = [
        'hiit',
        'arms & back',
        'lower body',
        'shoulders & core',
        'full body',
        'abs'
    ];
    
    // Categories to hide from the filter dropdown
    const hiddenCategories = ['upper body'];
    
    // Filter out 'All', normalize remaining categories, and exclude hidden ones
    const availableCategories = categories.filter(cat => {
        const normalized = cat.toLowerCase().trim();
        return cat !== 'All' && cat !== 'all' && !hiddenCategories.includes(normalized);
    });
    
    // Sort categories based on the defined order
    const sortedCategories = availableCategories.sort((a, b) => {
        const normalizedA = a.toLowerCase().trim();
        const normalizedB = b.toLowerCase().trim();
        
        const indexA = categoryOrder.indexOf(normalizedA);
        const indexB = categoryOrder.indexOf(normalizedB);
        
        // If both are in the order array, sort by their position
        if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
        }
        // If only one is in the order array, prioritize it
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        // If neither is in the order array, maintain original order
        return 0;
    });

    const categoryOptions = [
        { value: 'All', label: 'All' },
        ...sortedCategories.map(cat => ({ value: cat, label: formatLabel(cat) }))
    ];
    
    return (
        <div className={`tw-w-full tw-flex tw-justify-start ${isMobile ? 'tw-mb-4' : 'tw-mb-8'} ${isTablet ? 'tw-justify-center' : ''}`}>
            <div className={`tw-flex tw-items-center tw-gap-4 tw-w-auto ${isMobile ? 'tw-w-full tw-gap-2' : ''}`}>
                <span className={`tw-text-black tw-text-left tw-font-['Poppins'] tw-font-semibold tw-leading-[100%] tw-normal-case ${isMobile ? 'tw-text-sm tw-whitespace-nowrap' : 'tw-text-2xl'}`}>
                    Filter by:
                </span>
                <CustomSelect
                    value={currentCategory}
                    onChange={(e) => setCurrentCategory(e.target.value)}
                    options={categoryOptions}
                    isMobile={isMobile}
                />
            </div>
        </div>
    );
}

function Videos({ videos, currentCategory, onVideoSelect }) {
    const isMobile = window.innerWidth <= 768;
    const isTablet = window.innerWidth > 768 && window.innerWidth <= 1024;

    const displayVideos = useMemo(
        () => filterAndSortVideos(videos, currentCategory, 'newest', 30, undefined, false),
        [videos, currentCategory]
    );

    return (
        <ul className={`tw-grid tw-gap-6 tw-mx-auto tw-mb-24 tw-list-none tw-w-full tw-justify-center tw-pb-20 ${isMobile ? 'tw-grid-cols-2 tw-gap-4 tw-max-w-full tw-px-0' : isTablet ? 'tw-grid-cols-3 tw-gap-6 tw-max-w-[800px] tw-px-0' : 'tw-grid-cols-4 tw-max-w-[1200px] tw-px-0'}`}>
            {displayVideos.map((video, i) => (
                <VideoCard 
                    key={i}
                    video={video}
                    onClick={() => onVideoSelect(video)}
                    isMobile={isMobile}
                />
            ))}
        </ul>
    );
}

export default VideoLibrary;